/**
 * MobileBrokerDetail Component
 *
 * Mobile page for viewing and editing a broker's details.
 * Route: /settings/portfolio/reference-data/brokers/:brokerId
 *
 * Follows MobileStrategyDetail pattern adapted for broker domain:
 * URL param validation, actions popover, conflict banner, broker info/metadata
 * form, dirty tracking with save/discard footer, and unsaved changes dialog.
 */

// 1. External dependencies
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, MoreVertical, Globe, Settings2, ChevronRight } from 'lucide-react';

// 2. Workspace packages
import {
  Button,
  NumberInput,
  TextArea,
  Badge,
  useDisclosure,
  Breadcrumbs,
  BreadcrumbItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
} from '@enfomentis/ui';

// 3. Internal absolute — utilities & constants
import { getCurrencyInfo } from '@/utils';
import { toDisplayValue, toStorageValue } from '@/utils/moneyHelpers';
import { CURRENCY_DATABASE } from '@/constants';
import { findBrokerById } from '@/data/brokerCatalog';

// 4. Internal absolute — shared components
import { CurrencyChooserModal } from '@/components/shared/CurrencyChooser/CurrencyChooserModal';
import { UnsavedChangesDialog } from '@/components/shared/UnsavedChangesDialog';

// 5. Relative — domain components & context
import { useBrokers } from '../Brokers/context';
import { BrokerRenameModal } from '../Brokers/BrokerRenameModal';
import { BrokerFinancingFXModal } from '../Brokers/BrokerFinancingFXModal';

// =============================================================================
// Route Constants
// =============================================================================

const BROKER_LIST_ROUTE = '/settings/portfolio/reference-data/brokers';
const SETTINGS_ROUTE = '/settings';
const PORTFOLIO_ROUTE = '/settings/portfolio';

// =============================================================================
// Component
// =============================================================================

export const MobileBrokerDetail: React.FC = () => {
  const { brokerId } = useParams<{ brokerId: string }>();
  const navigate = useNavigate();
  const unsavedChangesDialog = useDisclosure();
  const renameModal = useDisclosure();

  // Pending navigation target when showing unsaved changes dialog
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Pending action when archive/restore is attempted while dirty
  const [pendingAction, setPendingAction] = useState<'archive' | 'restore' | null>(null);

  // Actions popover state
  const [isActionsPopoverOpen, setIsActionsPopoverOpen] = useState(false);

  // Currency chooser modal state
  const [showCurrencyChooser, setShowCurrencyChooser] = useState(false);

  // Financing & FX modal state
  const [showFinancingFXModal, setShowFinancingFXModal] = useState(false);

  // Context
  const {
    brokers,
    selectedBroker,
    selectedBrokerId,
    loading,
    linkedActiveAccounts,
    showArchived,
    setShowArchived,
    selectBroker,
    localDescription,
    localFeeMetadata,
    localOvernightDefault,
    localConversionDefault,
    setLocalDescription,
    setLocalFeeMetadata,
    setLocalOvernightDefault,
    setLocalConversionDefault,
    isDirty,
    isSaving,
    saveBroker,
    discardBrokerChanges,
    showConflictBanner,
    acceptExternalChanges,
    keepEditing,
    archiveBroker,
    restoreBroker,
    reloadBrokers,
  } = useBrokers();

  // ===========================================================================
  // URL Param Validation & Context Sync
  // ===========================================================================

  useEffect(() => {
    // Wait for brokers to load before any validation
    if (loading) {
      return;
    }

    if (!brokerId) {
      navigate(BROKER_LIST_ROUTE, { replace: true });
      return;
    }

    // Validate against full brokers array (not filtered)
    const broker = brokers.find((b) => b.id === brokerId);

    if (!broker) {
      // Broker not found — redirect to list
      navigate(BROKER_LIST_ROUTE, { replace: true });
      return;
    }

    // For archived deep-links: toggle showArchived first, then return
    // from this effect tick to let state settle before selecting
    if (broker.status === 'archived' && !showArchived) {
      setShowArchived(true);
      return;
    }

    // Sync to context if not already selected
    if (selectedBrokerId !== brokerId) {
      selectBroker(brokerId);
    }
  }, [brokerId, brokers, loading, selectedBrokerId, showArchived, setShowArchived, selectBroker, navigate]);

  // ===========================================================================
  // Derived Values
  // ===========================================================================

  const metadataCurrencyCode = localFeeMetadata.defaultAccountCurrency ?? 'USD';
  const metadataCurrencyInfo = getCurrencyInfo(metadataCurrencyCode, CURRENCY_DATABASE);
  const metadataPrecision = metadataCurrencyInfo?.minorUnit ?? localFeeMetadata.minimumBalance?.precision ?? 2;
  const metadataCurrencyHint = metadataCurrencyCode;

  // Fee configuration count for entry point
  const feeConfigCount = selectedBroker?.feeConfigurations?.length ?? 0;

  // ===========================================================================
  // Navigation Handlers
  // ===========================================================================

  const handleNavigateWithDirtyCheck = useCallback(
    (target: string): void => {
      if (isSaving) return;
      if (isDirty) {
        setPendingNavigation(target);
        unsavedChangesDialog.onOpen();
      } else {
        navigate(target);
      }
    },
    [isSaving, isDirty, navigate, unsavedChangesDialog],
  );

  const handleBack = useCallback((): void => {
    handleNavigateWithDirtyCheck(BROKER_LIST_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  const handleNavigateToSettings = useCallback((): void => {
    handleNavigateWithDirtyCheck(SETTINGS_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  const handleNavigateToPortfolio = useCallback((): void => {
    handleNavigateWithDirtyCheck(PORTFOLIO_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  const handleNavigateToFees = useCallback((): void => {
    if (!brokerId) return;
    handleNavigateWithDirtyCheck(`${BROKER_LIST_ROUTE}/${brokerId}/fees`);
  }, [brokerId, handleNavigateWithDirtyCheck]);

  const handleDiscard = useCallback((): void => {
    discardBrokerChanges();
    unsavedChangesDialog.onClose();
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    } else if (pendingAction === 'archive') {
      setPendingAction(null);
      void archiveBroker().then((success) => {
        if (success) navigate(BROKER_LIST_ROUTE, { replace: true });
      });
    } else if (pendingAction === 'restore') {
      setPendingAction(null);
      void restoreBroker();
    }
  }, [discardBrokerChanges, unsavedChangesDialog, pendingNavigation, navigate, pendingAction, archiveBroker, restoreBroker]);

  // ===========================================================================
  // Actions Handlers (Title Row Popover)
  // ===========================================================================

  const handleRenameClick = useCallback((): void => {
    setIsActionsPopoverOpen(false);
    renameModal.onOpen();
  }, [renameModal]);

  const handleArchiveClick = useCallback((): void => {
    setIsActionsPopoverOpen(false);
    if (isDirty) {
      setPendingAction('archive');
      unsavedChangesDialog.onOpen();
      return;
    }
    void archiveBroker().then((success) => {
      if (success) navigate(BROKER_LIST_ROUTE, { replace: true });
    });
  }, [isDirty, archiveBroker, navigate, unsavedChangesDialog]);

  const handleRestoreClick = useCallback((): void => {
    setIsActionsPopoverOpen(false);
    if (isDirty) {
      setPendingAction('restore');
      unsavedChangesDialog.onOpen();
      return;
    }
    void restoreBroker();
  }, [isDirty, restoreBroker, unsavedChangesDialog]);

  const handleRenameSuccess = useCallback((): void => {
    reloadBrokers();
  }, [reloadBrokers]);

  // ===========================================================================
  // Form Handlers
  // ===========================================================================

  const handleMetadataCurrencyChange = useCallback(
    (currencyCode: string): void => {
      const newInfo = getCurrencyInfo(currencyCode, CURRENCY_DATABASE);
      const newPrecision = newInfo?.minorUnit ?? 2;
      setLocalFeeMetadata({
        ...localFeeMetadata,
        defaultAccountCurrency: currencyCode,
        minimumBalance: {
          amount: localFeeMetadata.minimumBalance?.amount ?? 0,
          precision: newPrecision,
          currency: currencyCode,
        },
      });
      setShowCurrencyChooser(false);
    },
    [localFeeMetadata, setLocalFeeMetadata],
  );

  const handleFinancingFXConfirm = useCallback(
    (
      overnight: Parameters<typeof setLocalOvernightDefault>[0],
      conversion: Parameters<typeof setLocalConversionDefault>[0],
    ): void => {
      setLocalOvernightDefault(overnight);
      setLocalConversionDefault(conversion);
      setShowFinancingFXModal(false);
    },
    [setLocalOvernightDefault, setLocalConversionDefault],
  );

  // ===========================================================================
  // Loading / Not-Found Guard
  // ===========================================================================

  // Guard: prevent rendering stale broker during URL-param transitions
  if (!selectedBroker || selectedBroker.id !== brokerId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <Card radius="none" shadow="none" className="h-full flex flex-col">
      {/* Header - Breadcrumb Navigation + Page Title */}
      <CardHeader className="flex-col items-start pt-1 pb-1 border-b border-default-200">
        <div className="flex items-center">
          <Breadcrumbs variant="light" size="sm">
            <BreadcrumbItem
              onPress={handleNavigateToSettings}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Settings
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={handleNavigateToPortfolio}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Portfolio
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={handleBack}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Brokers
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>
        <div className="flex items-center justify-between w-full mt-1">
          <h1 className="text-lg font-semibold text-text-primary min-w-0">{selectedBroker.name}</h1>
          <div className="flex items-center gap-2 shrink-0">
            {selectedBroker.status === 'archived' && (
              <span className="text-xs text-text-tertiary">Archived</span>
            )}
            {/* Broker Actions Popover */}
            <Popover
              isOpen={isActionsPopoverOpen}
              onOpenChange={setIsActionsPopoverOpen}
              placement="bottom-end"
            >
              <PopoverTrigger>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  aria-label="Broker actions"
                  className="opacity-60 hover:opacity-100"
                >
                  <MoreVertical size={16} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-1">
                <div className="flex flex-col min-w-40">
                  <Button
                    size="md"
                    variant="light"
                    className="justify-start"
                    isDisabled={isSaving}
                    onPress={handleRenameClick}
                  >
                    Rename
                  </Button>
                  {selectedBroker.status !== 'archived' && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start text-warning-600"
                      isDisabled={isSaving}
                      onPress={handleArchiveClick}
                    >
                      Archive
                    </Button>
                  )}
                  {selectedBroker.status === 'archived' && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start text-success-600"
                      isDisabled={isSaving}
                      onPress={handleRestoreClick}
                    >
                      Restore
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardHeader>

      {/* Content */}
      <CardBody className="flex-1 px-1 pb-2">
        {/* Conflict Banner */}
        {showConflictBanner && (
          <div className="mx-4 mt-4 rounded-lg bg-warning-50 border border-warning-200 p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-warning-600 shrink-0" />
              <span className="text-sm text-warning-700">Updated in another tab</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="flat" onPress={keepEditing}>
                Keep editing
              </Button>
              <Button size="sm" color="warning" onPress={acceptExternalChanges}>
                Accept changes
              </Button>
            </div>
          </div>
        )}

        {/* Broker Detail Form */}
        <div className="space-y-4 pr-3 pt-2">
          {/* Basics Section */}
          <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
            <CardHeader className="px-1 pt-1 pb-1 min-h-10 text-base font-semibold text-text-primary border-b-1 border-default-200">
              Basics
            </CardHeader>
            <CardBody className="px-1 pb-0 space-y-3">
              {/* Account Currency */}
              <div className="min-w-0">
                <label className="block text-base text-text-primary mb-1">
                  Account Currency
                </label>
                <Button
                  variant="bordered"
                  className="w-full justify-between font-normal border-default-200"
                  endContent={<Globe className="w-4 h-4 text-text-tertiary shrink-0" />}
                  isDisabled={isSaving}
                  onPress={() => setShowCurrencyChooser(true)}
                >
                  {metadataCurrencyInfo ? (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{metadataCurrencyInfo.symbol}</span>
                      <span>{metadataCurrencyInfo.name}</span>
                      <span className="text-text-secondary">({metadataCurrencyCode})</span>
                    </div>
                  ) : (
                    <span>{metadataCurrencyCode}</span>
                  )}
                </Button>
              </div>

              {/* Minimum Balance */}
              <div className="min-w-0">
                <NumberInput
                  label="Minimum Balance"
                  labelPlacement="outside"
                  value={toDisplayValue(localFeeMetadata.minimumBalance?.amount, metadataPrecision)}
                  onValueChange={(val) => {
                    setLocalFeeMetadata({
                      ...localFeeMetadata,
                      minimumBalance: {
                        amount: toStorageValue(val, metadataPrecision) ?? 0,
                        precision: metadataPrecision,
                        currency: localFeeMetadata.minimumBalance?.currency ?? metadataCurrencyCode,
                      },
                    });
                  }}
                  endContent={<span className="text-base text-text-tertiary whitespace-nowrap leading-none">{metadataCurrencyHint}</span>}
                  formatOptions={{ minimumFractionDigits: metadataPrecision, maximumFractionDigits: metadataPrecision }}
                  classNames={{ input: 'text-base text-right' }}
                  hideStepper
                  isWheelDisabled
                  placeholder="0"
                />
              </div>

              {/* Description */}
              <TextArea
                label="Description"
                labelPlacement="outside-top"
                placeholder="Brief description of this broker..."
                value={localDescription}
                onValueChange={setLocalDescription}
                minRows={3}
                maxRows={3}
                className="[&_textarea]:text-base [&_textarea]:pt-2 p-1"
              />
            </CardBody>
          </Card>

          {/* Platform Fees Section */}
          <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
            <CardHeader className="flex items-center justify-between px-1 pt-1 pb-1 min-h-10 border-b-1 border-default-200">
              <span className="text-base font-semibold text-text-primary">Platform Fees</span>
              <Badge
                content=""
                color="success"
                size="sm"
                isInvisible={!(localOvernightDefault || localConversionDefault)}
              >
                <Button
                  variant="bordered"
                  size="sm"
                  startContent={<Settings2 size={14} />}
                  className="border-1 border-default-200"
                  isDisabled={isSaving}
                  aria-label={
                    localOvernightDefault || localConversionDefault
                      ? 'Financing & FX (configured)'
                      : 'Financing & FX'
                  }
                  onPress={() => setShowFinancingFXModal(true)}
                >
                  Financing &amp; FX
                </Button>
              </Badge>
            </CardHeader>
            <CardBody className="px-1 pb-0 space-y-3">
              {/* Fee NumberInputs */}
              <div className="space-y-10">
                <NumberInput
                  label="Monthly Fee"
                  labelPlacement="outside"
                  className="min-w-0"
                  value={toDisplayValue(localFeeMetadata.platformFees?.monthlyFee, metadataPrecision)}
                  onValueChange={(val) => {
                    setLocalFeeMetadata({
                      ...localFeeMetadata,
                      platformFees: {
                        ...localFeeMetadata.platformFees,
                        monthlyFee: toStorageValue(val, metadataPrecision) ?? 0,
                      },
                    });
                  }}
                  endContent={<span className="text-base text-text-tertiary whitespace-nowrap leading-none">{metadataCurrencyHint}</span>}
                  formatOptions={{ minimumFractionDigits: metadataPrecision, maximumFractionDigits: metadataPrecision }}
                  classNames={{ input: 'text-base text-right' }}
                  hideStepper
                  isWheelDisabled
                  placeholder="0"
                />

                <NumberInput
                  label="Data Fees"
                  labelPlacement="outside"
                  className="min-w-0"
                  value={toDisplayValue(localFeeMetadata.platformFees?.dataFees, metadataPrecision)}
                  onValueChange={(val) => {
                    setLocalFeeMetadata({
                      ...localFeeMetadata,
                      platformFees: {
                        ...localFeeMetadata.platformFees,
                        dataFees: toStorageValue(val, metadataPrecision) ?? 0,
                      },
                    });
                  }}
                  endContent={<span className="text-base text-text-tertiary whitespace-nowrap leading-none">{metadataCurrencyHint}</span>}
                  formatOptions={{ minimumFractionDigits: metadataPrecision, maximumFractionDigits: metadataPrecision }}
                  classNames={{ input: 'text-base text-right' }}
                  hideStepper
                  isWheelDisabled
                  placeholder="0"
                />

                <NumberInput
                  label="Inactivity Fee"
                  labelPlacement="outside"
                  className="min-w-0"
                  value={toDisplayValue(localFeeMetadata.platformFees?.inactivityFee, metadataPrecision)}
                  onValueChange={(val) => {
                    setLocalFeeMetadata({
                      ...localFeeMetadata,
                      platformFees: {
                        ...localFeeMetadata.platformFees,
                        inactivityFee: toStorageValue(val, metadataPrecision) ?? 0,
                      },
                    });
                  }}
                  endContent={<span className="text-base text-text-tertiary whitespace-nowrap leading-none">{metadataCurrencyHint}</span>}
                  formatOptions={{ minimumFractionDigits: metadataPrecision, maximumFractionDigits: metadataPrecision }}
                  classNames={{ input: 'text-base text-right' }}
                  hideStepper
                  isWheelDisabled
                  placeholder="0"
                />
              </div>

              {/* Platform Fee Notes */}
              <TextArea
                label="Platform Fee Notes"
                labelPlacement="outside-top"
                value={localFeeMetadata.platformFees?.notes ?? ''}
                onValueChange={(val) => {
                  setLocalFeeMetadata({
                    ...localFeeMetadata,
                    platformFees: {
                      ...localFeeMetadata.platformFees,
                      notes: val,
                    },
                  });
                }}
                minRows={3}
                maxRows={3}
                placeholder="Platform fee notes..."
                className="[&_textarea]:text-base [&_textarea]:pt-2 p-1"
              />
            </CardBody>
          </Card>

          {/* Fee Configurations Entry Point */}
          <Button
            variant="bordered"
            className="w-full justify-between font-normal border-default-200"
            endContent={<ChevronRight className="w-4 h-4 text-text-tertiary shrink-0" />}
            isDisabled={isSaving}
            onPress={handleNavigateToFees}
          >
            <span className="text-base">
              Fee Configurations
              {feeConfigCount > 0 && (
                <span className="ml-2 text-text-secondary">({feeConfigCount})</span>
              )}
            </span>
          </Button>

          {/* Catalog Source Indicator */}
          {selectedBroker.brokerReferenceId && (
            <div className="rounded-lg bg-default-100 p-3">
              <p className="text-base text-text-secondary">
                Imported from:{' '}
                <span className="font-medium text-text-primary">
                  {findBrokerById(selectedBroker.brokerReferenceId)?.name ?? 'Unknown Catalog Entry'}
                </span>
              </p>
            </div>
          )}

          {/* Linked Accounts Indicator */}
          {linkedActiveAccounts.length > 0 && (
            <div className="rounded-lg bg-default-100 p-3">
              <p className="text-base text-text-secondary">
                Used by:{' '}
                <span className="font-medium text-text-primary">
                  {linkedActiveAccounts.length <= 3
                    ? linkedActiveAccounts.map((a) => a.name).join(', ')
                    : `${linkedActiveAccounts.slice(0, 3).map((a) => a.name).join(', ')}, and ${linkedActiveAccounts.length - 3} more`}
                </span>
              </p>
            </div>
          )}
        </div>
      </CardBody>

      {/* Footer */}
      <CardFooter className="flex items-center justify-end pt-2 border-t border-default-200">
        <div className="flex items-center gap-1">
          {isDirty && <span className="text-xs text-warning-600">Unsaved</span>}
          <Button
            variant="flat"
            size="sm"
            onPress={discardBrokerChanges}
            isDisabled={!isDirty || isSaving}
          >
            Discard
          </Button>
          <Button
            color="primary"
            size="sm"
            onPress={saveBroker}
            isDisabled={!isDirty || isSaving}
            isLoading={isSaving}
          >
            Save
          </Button>
        </div>
      </CardFooter>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={unsavedChangesDialog.isOpen}
        onClose={() => {
          unsavedChangesDialog.onClose();
          setPendingNavigation(null);
          setPendingAction(null);
        }}
        onDiscard={handleDiscard}
      />

      {/* Broker Rename Modal */}
      <BrokerRenameModal
        isOpen={renameModal.isOpen}
        onClose={renameModal.onClose}
        broker={selectedBroker}
        onSave={handleRenameSuccess}
      />

      {/* Currency Chooser Modal */}
      <CurrencyChooserModal
        isOpen={showCurrencyChooser}
        onClose={() => setShowCurrencyChooser(false)}
        selectedCurrency={metadataCurrencyCode}
        onCurrencySelect={handleMetadataCurrencyChange}
        title="Account Currency"
      />

      {/* Financing & FX Modal */}
      <BrokerFinancingFXModal
        isOpen={showFinancingFXModal}
        onClose={() => setShowFinancingFXModal(false)}
        overnightDefault={localOvernightDefault}
        conversionDefault={localConversionDefault}
        accountCurrency={metadataCurrencyCode}
        onConfirm={handleFinancingFXConfirm}
      />
    </Card>
  );
};
